import React, { useRef, useState } from 'react';
import Tippy, { TippyProps } from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './tooltip.scss';
import Icon, { IconProps, IconType } from '../icon/Icon';
import Link, { LinkVariant } from '../link/Link';

export enum TooltipPlacement {
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
}

export enum TooltipTriggerType {
  MOUSEENTER = 'mouseenter',
  CLICK = 'click',
  FOCUSIN = 'focusin',
  FOCUS = 'focus',
  CUSTOM = 'custom',
}

export interface TooltipProps {
  onClick?: (event: React.MouseEvent) => void;
  content: React.ReactNode;
  children?: React.ReactNode;
  placement?: TooltipPlacement;
  trigger?: TooltipTriggerType;
  maxWidth?: number;
  interactive?: boolean;
  zIndex?: number;
  options?: Partial<TippyProps>;
  disable?: boolean;
  offsetY?: number;
  offsetX?: number;
  icon?: IconProps;
}

const TooltipContentWithClose = ({
  content,
  onClose,
}: {
  content: React.ReactNode;
  onClose: () => void;
}) => (
  <div className="tippy-inner has-close">
    <div>{content}</div>
    <Link
      className="tippy-close"
      aria-label="Close tooltip"
      variant={LinkVariant.BUTTON}
      onClick={onClose}
      icon={{ iconType: IconType.Close }}
      isSmall={true}
    />
  </div>
);

export const Tooltip: React.FC<TooltipProps> = ({
  onClick,
  content,
  children,
  placement = TooltipPlacement.TOP,
  trigger = TooltipTriggerType.MOUSEENTER,
  maxWidth = 300,
  interactive = false,
  zIndex = 1000,
  options = {},
  disable = false,
  offsetY = 0,
  offsetX = 0,
  icon,
}) => {
  const [instance, setInstance] = useState<any>(null);
  const tippyRef = useRef<any>(null);

  if (content === undefined || content === null || content === '') {
    return null;
  }

  return (
    <Tippy
      onCreate={(tippyInstance) => setInstance(tippyInstance)}
      ref={tippyRef}
      content={
        interactive ? (
          <TooltipContentWithClose
            content={content}
            onClose={() => instance?.hide()}
          />
        ) : (
          content
        )
      }
      placement={placement}
      appendTo={() => document.body}
      trigger={trigger}
      maxWidth={maxWidth}
      interactive={interactive}
      zIndex={zIndex}
      disabled={disable}
      offset={[offsetX, offsetY]}
      {...options}
    >
      <span
        className="tooltip-button-wrapper"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }}
      >
        {icon ? (
          <Icon iconType={icon.iconType} className={icon.className} />
        ) : (
          <>{children}</>
        )}
      </span>
    </Tippy>
  );
};

export default Tooltip;
